/**
 * kazangSession — Vuex module backing the kazang-api session 2FA flow.
 *
 * One OTP issued by POST /api/kazang/session/create authorises every
 * deposit in the next 2 hours via the returned sessionId. Sessions are
 * keyed by Api-Key (per kazang-api spec rule "one session per API key").
 *
 * Persistence: vuex-persist saves sessionsByApiKey to IndexedDB on every
 * mutation. Expired sessions are dropped on hydration. The app-wide
 * 1Hz ticker derives remainingSeconds from each session's expiresAt.
 */

const TICKER_INTERVAL_MS = 1000
const STORAGE_KEY = 'kazangSession'

export default {
  namespaced: true,

  state: () => ({
    // Keyed by Api-Key.
    sessionsByApiKey: {},
    // App-wide ticker handle. NOT persisted (vuex-persist ignores
    // functions/handles anyway).
    _tickerHandle: null,
    // Reactive nowMs updated by the ticker. Components read this via
    // the remainingSeconds getter.
    nowMs: Date.now()
  }),

  getters: {
    activeSessionFor: (state) => (apiKey) => {
      const s = state.sessionsByApiKey[apiKey]
      if (!s) return null
      if (s.status !== 'active') return null
      if (s.expiresAt <= state.nowMs) return null
      return s
    },

    remainingSeconds: (state) => (apiKey) => {
      const s = state.sessionsByApiKey[apiKey]
      if (!s || !s.expiresAt) return 0
      return Math.max(0, Math.floor((s.expiresAt - state.nowMs) / 1000))
    },

    isActive: (state, getters) => (apiKey) => {
      return !!getters.activeSessionFor(apiKey)
    }
  },

  mutations: {
    SET_SESSION(state, { apiKey, session }) {
      if (!apiKey) return
      state.sessionsByApiKey = { ...state.sessionsByApiKey, [apiKey]: session }
    },
    CLEAR_SESSION(state, apiKey) {
      if (!apiKey) return
      const next = { ...state.sessionsByApiKey }
      delete next[apiKey]
      state.sessionsByApiKey = next
    },
    SET_NOW_MS(state, nowMs) {
      state.nowMs = nowMs
    },
    SET_TICKER_HANDLE(state, handle) {
      state._tickerHandle = handle
    }
  },

  actions: {
    // ensureSession, refreshCountdown, invalidate, depositWith
    // — added in later tasks.
    /**
     * Drop expired sessions from persisted state, then start the app-wide
     * 1Hz ticker if any session is still active. Call once from main.js.
     */
    bootstrap({ commit, state, dispatch }) {
      const now = Date.now()
      for (const [apiKey, s] of Object.entries(state.sessionsByApiKey)) {
        if (!s || !s.expiresAt || s.expiresAt <= now) {
          commit('CLEAR_SESSION', apiKey)
        }
      }
      if (Object.keys(state.sessionsByApiKey).length > 0) {
        dispatch('startTicker')
      }
    },

    /**
     * Start the app-wide 1Hz ticker. Idempotent — calling twice does not
     * spawn two intervals. Mounting/unmounting modals does NOT call this.
     */
    startTicker({ commit, state }) {
      if (state._tickerHandle) return
      const handle = setInterval(() => {
        commit('SET_NOW_MS', Date.now())
      }, TICKER_INTERVAL_MS)
      commit('SET_TICKER_HANDLE', handle)
    },

    /**
     * Stop the ticker. Safe to call when no ticker is running.
     */
    stopTicker({ commit, state }) {
      if (state._tickerHandle) {
        clearInterval(state._tickerHandle)
        commit('SET_TICKER_HANDLE', null)
      }
    },

    /**
     * Resolve the merchant's active session, minting a new one if needed.
     * Callers (DepositForm, approveExpenseRequest) use this as the single
     * entry point to "make a session available" before issuing a deposit.
     *
     * Behavior:
     *  - If apiKey has an active session: return immediately.
     *  - If otp is omitted: POST /session/create, return { status: 'otp_required',
     *    sessionRequestId, phoneHint, expiresAt } so the caller can show the
     *    OTP modal and re-invoke with otp.
     *  - If otp is provided: POST /session/confirm with { sessionRequestId, otp }.
     *    On 200, store the active session and return { ok: true, sessionId }.
     *
     * Throws on 401/429/500 — the caller surfaces the error to the user.
     */
    async ensureSession({ commit, state, dispatch }, { apiKey, otp, sessionRequestId } = {}) {
      if (!apiKey) throw new Error('ensureSession: apiKey is required')

      // Fast path — already active.
      const existing = state.sessionsByApiKey[apiKey]
      if (existing && existing.status === 'active' && existing.expiresAt > Date.now()) {
        return { ok: true, sessionId: existing.sessionId }
      }

      const headers = { 'Api-Key': apiKey, 'X-API-KEY': apiKey, 'Content-Type': 'application/json' }

      if (!otp) {
        // Phase 1 — issue a fresh session.
        const url = `${pettyCashApi}api/kazang/session/create`
        const { data } = await axios.post(url, {}, { headers })
        // Stash the in-flight PENDING row so a confirm call has context.
        commit('SET_SESSION', {
          apiKey,
          session: {
            status: 'pending',
            sessionId: null,
            sessionRequestId: data.sessionRequestId,
            phoneHint: data.phoneHint || null,
            expiresAt: data.expiresAt ? new Date(data.expiresAt).getTime() : Date.now() + 2 * 60 * 1000,
            message: data.message || 'OTP sent to your registered WhatsApp number.'
          }
        })
        dispatch('startTicker')
        return data
      }

      // Phase 2 — confirm the OTP.
      if (!sessionRequestId) {
        const pending = state.sessionsByApiKey[apiKey]
        sessionRequestId = pending ? pending.sessionRequestId : null
      }
      if (!sessionRequestId) {
        throw new Error('ensureSession: sessionRequestId is required when otp is provided')
      }
      const url = `${pettyCashApi}api/kazang/session/confirm`
      try {
        const { data } = await axios.post(url, { sessionRequestId, otp }, { headers })
        const expiresAtMs = data.expiresAt ? new Date(data.expiresAt).getTime() : Date.now() + 2 * 60 * 60 * 1000
        commit('SET_SESSION', {
          apiKey,
          session: {
            status: 'active',
            sessionId: data.sessionId,
            sessionRequestId,
            phoneHint: (state.sessionsByApiKey[apiKey] || {}).phoneHint || null,
            expiresAt: expiresAtMs,
            message: data.message || 'Session active.'
          }
        })
        dispatch('startTicker')
        return { ok: true, sessionId: data.sessionId }
      } catch (err) {
        // 401 — wrong OTP or expired session request. Drop the PENDING row.
        if (err && err.response && err.response.status === 401) {
          commit('CLEAR_SESSION', apiKey)
        }
        throw err
      }
    },

    /**
     * Fetch the server-authoritative countdown for an api_key, anchor the
     * local tick to the server's expiresAt, and clear the session if the
     * server says it's terminal (phase=none or phase=expired).
     */
    async refreshCountdown({ commit, dispatch, state }, apiKey) {
      if (!apiKey) return null
      const headers = { 'Api-Key': apiKey, 'X-API-KEY': apiKey, 'Content-Type': 'application/json' }
      const url = `${pettyCashApi}api/kazang/session/countdown`
      try {
        const { data } = await axios.post(url, {}, { headers })
        if (data.phase === 'none' || data.phase === 'expired') {
          commit('CLEAR_SESSION', apiKey)
          dispatch('stopTickerIfIdle')
          return data
        }
        const existing = state.sessionsByApiKey[apiKey]
        if (existing && data.expiresAt) {
          commit('SET_SESSION', {
            apiKey,
            session: {
              ...existing,
              status: data.active ? 'active' : (data.phase === 'otp' ? 'pending' : existing.status),
              expiresAt: new Date(data.expiresAt).getTime()
            }
          })
        }
        return data
      } catch (err) {
        // Network or 5xx — leave the local session alone; the ticker keeps
        // the UI honest based on local expiresAt.
        return null
      }
    },

    /**
     * Drop the session for an api_key. Called when /sendMoney returns 401
     * "Session is invalid, expired, or revoked". Stops the ticker if no
     * sessions remain.
     */
    invalidate({ commit, dispatch }, apiKey) {
      if (!apiKey) return
      commit('CLEAR_SESSION', apiKey)
      dispatch('stopTickerIfIdle')
    },

    /**
     * Stop the ticker if there are no remaining sessions. Used by
     * refreshCountdown and invalidate.
     */
    stopTickerIfIdle({ state, dispatch }) {
      if (Object.keys(state.sessionsByApiKey).length === 0) {
        dispatch('stopTicker')
      }
    },

    /**
     * Single entry point for deposit callers. Ensures a session is active
     * (calling onOtpRequired if a new OTP is needed), then POSTs the
     * deposit with the sessionId. On 401-session-invalid, invalidates,
     * re-issues the OTP, and retries once.
     *
     * @param {Object} args
     * @param {string} args.apiKey - merchant's business API key
     * @param {Object} args.body - { branchId, localid, provider, reason, account, amount }
     * @param {Function} [args.onOtpRequired] - ({ phoneHint, sessionRequestId }) => void.
     *        Called when a fresh OTP must be entered. The caller shows a modal
     *        and re-invokes depositWith with the otp once the user submits.
     * @param {Function} [args.onSessionRequired] - () => void. Called when
     *        the backend signals that no session is available and the caller
     *        must orchestrate an end-to-end re-auth.
     * @returns {Object} the deposit response from the backend
     */
    async depositWith({ state, dispatch }, { apiKey, body, otp, sessionRequestId, onOtpRequired, onSessionRequired } = {}) {
      if (!apiKey) throw new Error('depositWith: apiKey is required')
      if (!body) throw new Error('depositWith: body is required')

      // Step 1 — ensure a session.
      if (!state.sessionsByApiKey[apiKey] || state.sessionsByApiKey[apiKey].status !== 'active') {
        // No active session — call ensureSession.
        const result = await dispatch('ensureSession', { apiKey })
        if (result && result.status === 'otp_required') {
          // Caller must show the modal and re-invoke with otp.
          if (typeof onOtpRequired === 'function') {
            onOtpRequired({ phoneHint: result.phoneHint, sessionRequestId: result.sessionRequestId })
          }
          throw new Error('OTP_REQUIRED')
        }
        // result.ok === true — we have a sessionId already.
      }

      const active = state.sessionsByApiKey[apiKey]
      if (!active || !active.sessionId) {
        // Defensive: the session should be active by now.
        if (typeof onSessionRequired === 'function') onSessionRequired()
        throw new Error('SESSION_REQUIRED')
      }

      // Step 2 — POST the deposit with sessionId.
      try {
        return await dispatch('Kazang/depositWithSession', {
          sessionId: active.sessionId,
          ...body
        }, { root: true })
      } catch (err) {
        // Step 3 — if 401-session-invalid, recover and re-prompt.
        const status = err && err.response && err.response.status
        const message = err && err.response && err.response.data && err.response.data.message
        if (status === 401 && message && /session/i.test(message)) {
          await dispatch('invalidate', apiKey)
          const result = await dispatch('ensureSession', { apiKey })
          if (result && result.status === 'otp_required') {
            if (typeof onOtpRequired === 'function') {
              onOtpRequired({ phoneHint: result.phoneHint, sessionRequestId: result.sessionRequestId })
            }
            throw new Error('OTP_REQUIRED')
          }
          // Retry once with the fresh sessionId.
          const fresh = state.sessionsByApiKey[apiKey]
          if (fresh && fresh.sessionId) {
            return await dispatch('Kazang/depositWithSession', {
              sessionId: fresh.sessionId,
              ...body
            }, { root: true })
          }
          if (typeof onSessionRequired === 'function') onSessionRequired()
          throw new Error('SESSION_REQUIRED')
        }
        throw err
      }
    }
  }
}
